import { useEffect, useRef, useState } from "react";
import { ExternalLink, Copy, CheckCircle } from "lucide-react";
import type { GatewayProviderMeta } from "../../../constants/gatewayProviders";
import { config } from "../../../config/config";
import { useToast } from "../../../contexts/toast/Context";

interface StepHelpProps {
  provider: GatewayProviderMeta;
  gymPublicId?: string;
}

export const StepHelp = ({ provider, gymPublicId }: StepHelpProps) => {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const webhookUrl = gymPublicId
    ? `${config.API_URL}/gateway/webhooks/${provider.key}/${gymPublicId}`
    : null;

  const handleCopy = async () => {
    if (!webhookUrl) return;
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      showToast("URL copiada al portapapeles", "success");
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("Error al copiar", "error");
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-size-sm text-global-secondary font-medium">
        Configurar {provider.displayName} — {provider.productName}
      </p>

      {provider.helpSteps && (
        <ol className="space-y-3">
          {provider.helpSteps.map((stepText, index) => {
            const isWebhookStep =
              provider.key === "mercadopago" &&
              index === provider.helpSteps!.length - 1 &&
              webhookUrl;

            return (
              <li key={index} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-global-overlay-secondary text-global-accent text-size-2xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {index + 1}
                </span>
                <div className="space-y-1.5">
                  <span className="text-size-sm text-global-secondary">
                    {stepText}
                  </span>
                  {isWebhookStep && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <code className="flex-1 text-size-2xs px-2 py-1.5 rounded-md bg-global-overlay-secondary border border-global-secondary text-global-accent font-mono break-all">
                        {webhookUrl}
                      </code>
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="shrink-0 p-1.5 rounded-md border border-global-secondary hover:bg-global-overlay-secondary transition-colors"
                        title="Copiar URL"
                      >
                        {copied ? (
                          <CheckCircle className="w-3.5 h-3.5 text-global-success" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-global-muted" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {provider.helpUrl && (
        <a
          href={provider.helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-size-sm text-global-accent hover:underline transition-colors duration-200"
        >
          Ir al panel de {provider.displayName}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
};
